import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import * as arise from '@/arise/api';
import { Badge, EMPTY_PROFILE_V2, Profile, ProfileV2, StorageProfile, Title } from '@/models/profile';
import { AppDispatch, RootState } from '@/state/store';
import { queueAchievement } from '@/state/features/app';
import { processProfileData } from '@/utils/profile';
import { sendEventV2 } from '@/utils/analytics';
import { storeProfileV2InLocalStorage } from '@/utils/localProfileV2';
import { IS_DISCORD_ACTIVITY } from '@/globals';
import { getTranslatedBadge, getTranslatedTitle, getUITranslatedText } from '@/components/TranslatedText';

interface ProfileState {
    currentUserProfile?: Profile;
    currentUserProfileV2?: ProfileV2;
    // The name of the account we are signed in through (Discord), which always wins over the name
    // stored against the Arise profile - see setProfileV2DisplayName
    signedInDisplayName?: string;
    currentUserProfileCheckInProgress: boolean;
    currentUserProfileCheckComplete: boolean;

    availableBadges?: Badge[]; // All badges which could be earned
    availableTitles?: Title[]; // All titles which could be earned

    profileByUserId: Record<string, StorageProfile>;
    isFetchingProfileById: Record<string, boolean>;
}

const initialState: ProfileState = {
    currentUserProfileCheckInProgress: false,
    currentUserProfileCheckComplete: false,
    currentUserProfileV2: EMPTY_PROFILE_V2,
    availableBadges: [],
    availableTitles: [],
    profileByUserId: {},
    isFetchingProfileById: {},
};

export const initialProfileState = initialState;

export const awardBadge =
    (badgeRank: string) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
        // Send to API
        arise.addProfileBadge(badgeRank).catch(() => {
            console.warn('Failed to send badge to API');
        });

        // Send stat
        sendEventV2('badge_awarded', [badgeRank]);

        const profileState = getState()?.profile;

        // Check if valid badge
        const availableBadges = profileState.availableBadges;

        if (!Array.isArray(availableBadges)) {
            console.warn('Cannot award badge - availableBadges not an array');
            return;
        }

        const hydratedBadge = availableBadges.find((availableBadge: Badge) => availableBadge.rank === badgeRank);

        if (!hydratedBadge) {
            console.warn(`Cannot award badge - "${badgeRank}" is not a valid badge rank`);
            return;
        }

        // Check if already earned
        const profileBadges = profileState?.currentUserProfileV2?.badges ?? [];
        const alreadyEarned = profileBadges.find((b) => b.rank === badgeRank);
        if (alreadyEarned) {
            console.log(`Badge already earned: ${alreadyEarned.name}`);
            return;
        }

        // Update local state
        dispatch(profile.actions.awardBadgeLocal(hydratedBadge));

        // Send achievement
        const translation_badgeActionPrefix = getUITranslatedText(
            'notification_badgeActionPrefix',
            'You earned the badge:',
        );

        const translatedBadge = getTranslatedBadge(hydratedBadge);
        dispatch(
            queueAchievement({
                actionPrefix: `${translation_badgeActionPrefix} `,
                action: `${translatedBadge.name}`,
                type: 'success',
                icon: 'badge',
                onClickAction: 'open-profile',
            }),
        );
    };

export const awardTitle =
    (titleId: string) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
        // Send to API
        arise.addProfileTitle(titleId).catch(() => {
            console.warn('Failed to send title to API');
        });

        // Send stat
        sendEventV2('title_awarded', [titleId]);

        const profileState = getState()?.profile;
        const availableTitles = profileState.availableTitles;

        if (!Array.isArray(availableTitles)) {
            console.warn('Cannot award title - availableTitles not an array');
            return;
        }

        // Check if valid title
        const hydratedTitle = availableTitles.find((availableTitle: Title) => availableTitle.id === titleId);

        if (!hydratedTitle) {
            console.warn(`Cannot award title - "${titleId}" is not a valid title ID`);
            return;
        }

        // Check if already earned
        const profileTitles = profileState?.currentUserProfileV2?.titles ?? [];
        const alreadyEarned = profileTitles.find((b) => b.id === titleId);
        if (alreadyEarned) {
            console.log(`Title already earned: ${alreadyEarned.title}`);
            return;
        }

        // Update local state
        dispatch(profile.actions.awardTitleLocal(hydratedTitle));

        const translation_titleActionPrefix = getUITranslatedText(
            'notification_titleActionPrefix',
            'You earned the title:',
        );

        const translatedTitle = getTranslatedTitle(hydratedTitle);
        // Send achievement
        dispatch(
            queueAchievement({
                actionPrefix: `${translation_titleActionPrefix} `,
                action: `"${translatedTitle.title}"`,
                type: 'success',
                icon: 'title',
                onClickAction: 'open-profile',
            }),
        );
    };

export const setMissionComplete =
    () =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
        // Send to API
        arise.setProfileMissionCompleted().catch(() => {
            console.warn('Failed to send mission complete to API');
        });

        // Check if already earned
        const isAlreadyComplete = getState()?.profile?.currentUserProfileV2.missionCompleted;
        if (isAlreadyComplete) {
            console.log('Mission is already complete');
            return;
        }

        // Update local state
        dispatch(profile.actions.setMissionCompletedLocal());

        // Send achievement
        const translation_questCompleteActionPrefix = getUITranslatedText(
            'notification_questCompleteActionPrefix',
            'You completed the Quest!',
        );
        const translation_missionCompleteActionPrefix = getUITranslatedText(
            'notification_missionCompleteActionPrefix',
            'You completed the Mission!',
        );

        dispatch(
            queueAchievement({
                actionPrefix: '',
                action: IS_DISCORD_ACTIVITY
                    ? translation_questCompleteActionPrefix
                    : translation_missionCompleteActionPrefix,
                type: 'success',
                icon: IS_DISCORD_ACTIVITY ? 'quest' : 'mission',
                onClickAction: 'open-profile',
            }),
        );
    };

export const setProfileTitle = createAsyncThunk<boolean, string>('profile/setProfileTitle', async (id: string) => {
    const result = await arise.setProfileTitle(id);
    return result.success;
});

export const setProfileFaction = createAsyncThunk<boolean, string>('profile/setProfileFaction', async (id: string) => {
    const result = await arise.setProfileFaction(id);
    return result.success;
});

export const loadCurrentUserProfile = createAsyncThunk<Profile, [userId: string]>('profile/load', async (userId) => {
    const profiles = await arise.getProfiles(userId);
    return profiles[0] ?? undefined;
});

export const loadCurrentUserProfileV2 = createAsyncThunk<ProfileV2, void>('profileV2/load', async (_, thunkApi) => {
    // Render this user's cached profile straight away, then replace it with the API's version so
    // their badges and titles are up to date (and are never the previous user's)
    const cachedProfile = arise.getCachedCurrentUserProfileV2();
    if (cachedProfile) {
        thunkApi.dispatch(profile.actions.setCurrentUserProfileV2(cachedProfile));
    }

    const remoteProfile = await arise.getCurrentUserProfileV2();
    if (!remoteProfile) throw new Error('No profile found');
    return remoteProfile;
});

// A different user can sign in while a profile request is in flight, so never apply a profile which
// belongs to anyone other than the user we are currently authenticated as
function isCurrentUsersProfile(profile?: ProfileV2) {
    // No user id means locally built state (e.g. EMPTY_PROFILE_V2), which is always safe to apply
    if (!profile?.userId) return true;
    return profile.userId === arise.getCurrentUserId();
}

function applyCurrentUserProfileV2(state: ProfileState, profile: ProfileV2) {
    if (!isCurrentUsersProfile(profile)) {
        console.warn('applyCurrentUserProfileV2: ignoring profile for a different user');
        return;
    }
    state.currentUserProfileV2 = {
        ...profile,
        displayName: state.signedInDisplayName || profile.displayName,
    };
}

export const getAvailableBadges = createAsyncThunk<Badge[], void>('profile/getAvailableBadges', async () => {
    const result = await arise.getAvailableBadges();
    if (!Array.isArray(result)) {
        console.warn('getAvailableBadges response not valid');
        return [];
    }
    return result;
});

export const getAvailableTitles = createAsyncThunk<Title[], void>('profile/getAvailableTitles', async () => {
    const result = await arise.getAvailableTitles();
    if (!Array.isArray(result)) {
        console.warn('getAvailableTitles response not valid');
        return [];
    }
    return result;
});

export const loadUserProfiles = createAsyncThunk<Profile[] | null, string[]>(
    'profile/loadUserProfiles',
    async (userIds, thunkApi) => {
        const state = thunkApi.getState() as RootState;

        const ttl = Date.now() + 1000 * 60;
        const cleanedUserIds = userIds.filter((id) => {
            return (
                !state.profile.isFetchingProfileById[id] &&
                (!state.profile.profileByUserId[id] ||
                    (state.profile.profileByUserId[id] && state.profile.profileByUserId[id].fetchedAt > ttl))
            );
        });

        if (cleanedUserIds.length === 0) {
            return null;
        }

        thunkApi.dispatch(profile.actions.markProfilesAsFetching(cleanedUserIds));

        return await arise.getProfiles(cleanedUserIds);
    },
);

const profile = createSlice({
    name: 'profile',
    initialState,
    reducers: {
        markProfilesAsFetching: (state, action) => {
            action.payload.forEach((id) => {
                state.isFetchingProfileById[id] = true;
            });
        },
        updateProfile: (state, action: PayloadAction<Profile>) => {
            const data = action.payload as Profile;

            const profile = processProfileData(data);

            const now = Date.now();
            profile.fetchedAt = now;

            // Not currenlty using the profile cache so commenting this out to avoid using unnecessary memory
            // state.profileByUserId[profile.userID] = profile;

            if (state.currentUserProfile?.id === profile.id) {
                state.currentUserProfile = profile;
            }
        },
        resetProfileState: (state) => {
            const newState = {
                ...initialState,
                profileByUserId: state.profileByUserId,
                isFetchingProfileById: state.isFetchingProfileById,
            };
            Object.assign(state, newState);
        },
        setCurrentUserProfileV2: (state, action: PayloadAction<ProfileV2>) => {
            applyCurrentUserProfileV2(state, action.payload);
        },
        setProfileV2DisplayName: (state, action: PayloadAction<string>) => {
            // This is the name of the account the user actually signed in with, so remember it and
            // keep it even when a profile loaded from the API or the cache carries an older name
            state.signedInDisplayName = action.payload;
            if (state.currentUserProfileV2) {
                state.currentUserProfileV2.displayName = action.payload;
            }
        },
        awardBadgeLocal: (state, action: PayloadAction<Badge>) => {
            if (Array.isArray(state?.currentUserProfileV2?.badges)) {
                state.currentUserProfileV2.badges.push(action.payload);
                storeProfileV2InLocalStorage(state.currentUserProfileV2, arise.getCurrentUserId());
            } else {
                console.log('awardBadgeLocal skipping as badges array not valid');
            }
        },
        awardTitleLocal: (state, action: PayloadAction<Title>) => {
            if (Array.isArray(state?.currentUserProfileV2?.titles)) {
                state.currentUserProfileV2.titles.push(action.payload);
                storeProfileV2InLocalStorage(state.currentUserProfileV2, arise.getCurrentUserId());
            } else {
                console.log('awardTitleLocal skipping as titles array not valid');
            }
        },
        setMissionCompletedLocal: (state) => {
            if (state.currentUserProfileV2) {
                state.currentUserProfileV2.missionCompleted = true;
                storeProfileV2InLocalStorage(state.currentUserProfileV2, arise.getCurrentUserId());
            } else {
                console.log('setMissionCompleted skipping currentUserProfileV2 not loaded');
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadCurrentUserProfile.pending, (state) => {
            state.currentUserProfileCheckInProgress = true;
            state.currentUserProfileCheckComplete = false;
        });

        builder.addCase(loadCurrentUserProfile.fulfilled, (state, action) => {
            state.currentUserProfile = action.payload;
            state.currentUserProfileCheckInProgress = false;
            state.currentUserProfileCheckComplete = true;
        });

        builder.addCase(loadCurrentUserProfile.rejected, (state, action) => {
            console.error('Failed to load current user profile', action.error);
            state.currentUserProfileCheckInProgress = false;
            state.currentUserProfileCheckComplete = true;
        });
        builder.addCase(loadCurrentUserProfileV2.fulfilled, (state, action) => {
            applyCurrentUserProfileV2(state, action.payload);
        });

        builder.addCase(loadUserProfiles.fulfilled, (state, action) => {
            action.meta.arg.forEach((id) => {
                state.isFetchingProfileById[id] = false;
            });

            const now = Date.now();

            action.payload?.forEach((profile) => {
                const p = profile as StorageProfile;
                p.fetchedAt = now;
                state.profileByUserId[p.userID] = p;
            });
        });

        builder.addCase(loadUserProfiles.rejected, (state, action) => {
            console.error(action.error);
            action.meta.arg.forEach((id) => {
                state.isFetchingProfileById[id] = false;
            });
        });

        builder.addCase(getAvailableBadges.fulfilled, (state, action) => {
            state.availableBadges = action.payload;
        });

        builder.addCase(getAvailableTitles.fulfilled, (state, action) => {
            state.availableTitles = action.payload;
        });
    },
});

export const { updateProfile, resetProfileState, setCurrentUserProfileV2, setProfileV2DisplayName } = profile.actions;
export const { actions: profileActions, reducer: profileReducer } = profile;

export default profileReducer;
